import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, Flex, Input, Text } from "@chakra-ui/react";
import { HiOutlineUsers, HiOutlineChatBubbleLeftRight, HiOutlineSquares2X2, HiOutlineBolt } from "react-icons/hi2";
import { useLocation, useNavigate } from "react-router-dom";
import InClassHeader from "./InClassHeader";
import SessionChatPanel from "./panels/SessionChatPanel";
import AttendancePanel from "./panels/AttendancePanel";
import FocusBanner from "./panels/FocusBanner";
import RoomDock from "./panels/RoomDock";
import ClusterRail from "./chat/ClusterRail";
import InClassCanvas from "./canvas/InClassCanvas";
import { useRoom } from "./useRoom";
import { useRoomStream } from "./useRoomStream";
import { useSubRooms } from "./useSubRooms";
import RoomsPanel from "./rooms/RoomsPanel";
import RoomStrip from "./rooms/RoomStrip";
import RoomBanner from "./rooms/RoomBanner";
import { useLiveActivity, questionLabel } from "./activities/useLiveActivity";
import ActivityStudio from "./activities/ActivityStudio";
import LiveActivityPanel from "./activities/LiveActivityPanel";
import ActivityStage from "./activities/ActivityStage";
import StudentActivity from "./activities/StudentActivity";
import { useBoardSync } from "./useBoardSync";
import { useBoardOps } from "./useBoardOps";
import { useQuestionClusters } from "./chat/useQuestionClusters";
import { useViewport } from "./canvas/useViewport";
import { prepareContent } from "./canvas/openContent";
import { roomApi } from "./roomApi";
import { useFullscreen } from "./useFullscreen";
import { spaceApi } from "../space/spaceApi";
import BoardToolbar from "../board/BoardToolbar";
import BoardCanvas from "../board/BoardCanvas";
import { useBoard } from "../board/useBoard";
import { boundsOf } from "../board/boardGeometry";
import { TOOLS, SHAPES, DEFAULT_COLOUR, REVERTS_AFTER_USE } from "../board/boardTools";
import { c, glassSurface, useClassroomColorMode } from "../classroomColors";
import { useClassroomToast } from "../classroomToast";
import "../classroom-theme.css";

/** Where the cards on the left start: clear of the header. */
const BELOW_HEADER = "84px";
const SIDE_GAP = "20px";
/** How far the board reaches past the first screen, in board pixels. */
const BOARD_REACH = 20000;

/**
 * The in-class room (wireframe 265:30): the whole window is the canvas, and
 * everything else floats on it — the header along the top, who is here and
 * the chat as cards on the left that fold away, the question monitor beside
 * them, and the board tools low on the right.
 *
 * This is the only component that knows about all of those. Each part owns
 * its own state through a hook — chat, clusters, viewport, content, board —
 * so any of them can be tested, replaced or reused without the others. The
 * page's own state is just the three things the parts have to agree on: the
 * active tool, the canvas pattern, and which cluster is open.
 */
const InClassPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isDark, toggleColorMode } = useClassroomColorMode();
  const toast = useClassroomToast(isDark);

  const {
    courseGuid,
    classroomName = "Class",
    sessionName = "In class",
    userType = "INSTRUCTOR",
    sessionGuid,
    sessionState = null,
  } = location.state || {};
  const isInstructor = userType === "INSTRUCTOR";

  // Back to the space this session belongs to.
  const backToSpace = useCallback(
    () =>
      navigate(isInstructor ? "/classroom/teacher/class" : "/classroom/student/class", {
        state: { courseGuid, classroomName, userType },
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [courseGuid, classroomName, userType]
  );

  // The room fills the screen. The way in asks for full screen on its click
  // (a browser allows it only then); this asks again in case the room was
  // reached another way, and the page leaves full screen with the room.
  const fullscreen = useFullscreen();
  const inRoom = useRef(false);
  useEffect(() => {
    inRoom.current = true;
    fullscreen.enter();
    return () => {
      inRoom.current = false;
      // A tick later, so a remount (React's dev double run) doesn't drop it.
      setTimeout(() => !inRoom.current && fullscreen.exit(), 0);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Opening the room doesn't start the session: the teacher can get it
  // ready or try it out first, and goes live when the class begins. Going
  // live is what shows it as live in the space, for students to join.
  const [isLive, setIsLive] = useState(sessionState === "LIVE");
  const goLive = async () => {
    try {
      await spaceApi.startSession(sessionGuid);
      setIsLive(true);
      toast({ title: "You're live", description: "Students can join from the space now.", status: "success", duration: 2600 });
    } catch (e) {
      toast({ title: "Couldn't go live", description: e.message, status: "error" });
    }
  };

  /** End the class for everyone: the session is done, and its recap waits
   *  in the space. */
  const endSession = async () => {
    try {
      if (sessionGuid) await spaceApi.endSession(sessionGuid);
      toast({ title: "Session ended", description: "Its recap is in the space.", status: "success", duration: 2600 });
      backToSpace();
    } catch (e) {
      toast({ title: "Couldn't end the session", description: e.message, status: "error" });
    }
  };
  // A student is told when the teacher ends the class.
  const [classEnded, setClassEnded] = useState(false);

  const currentUser = {
    guid: localStorage.getItem("userGuid") || "me",
    name: "You",
    initial: localStorage.getItem("firstLetter") || "?",
  };

  // Sub-rooms. A student is wherever the teacher put them; the teacher is
  // wherever they chose to go, the class to start with.
  const subRooms = useSubRooms({ sessionGuid, currentUserGuid: currentUser.guid });
  // A live activity — a race, a solo solve, an escape room — as this person
  // may see it; the stream says when to read it again.
  const live = useLiveActivity({ sessionGuid });
  // The teacher's big screen over the canvas: null, the live view, or results.
  const [stage, setStage] = useState(null);
  // Setting up the next activity while one is still on (running, or ended
  // with its results): the studio, with the way back.
  const [composing, setComposing] = useState(false);
  const [teacherRoomId, setTeacherRoomId] = useState(null);
  const viewRoomId = isInstructor ? teacherRoomId : subRooms.myRoomId;
  const viewRoom = subRooms.rooms.find((r) => r.id === viewRoomId) || null;
  const viewRoomIndex = subRooms.rooms.findIndex((r) => r.id === viewRoomId);
  // The teacher's room closing under them takes them back to the class.
  useEffect(() => {
    if (teacherRoomId && !subRooms.rooms.some((r) => r.id === teacherRoomId)) setTeacherRoomId(null);
  }, [teacherRoomId, subRooms.rooms]);
  // Read by the stream handlers, which outlive a render.
  const viewRoomRef = useRef(viewRoomId);
  viewRoomRef.current = viewRoomId;
  // The class board is the teacher's to write; a room's board is everyone's
  // in it — that is what a room is for.
  const canDraw = isInstructor || Boolean(viewRoomId);

  // The room is live when it has a session to be live in; without one it runs
  // in memory, which is what a preview or the standalone board wants.
  const boardOps = useBoardOps({ sessionGuid, subRoomGuid: viewRoomId, enabled: canDraw });
  const board = useBoard({ onOp: boardOps.emit });

  // A board pushed from the server lands straight on the canvas. The teacher
  // ignores their own echo: they already have it, and applying it would fight
  // whatever they have drawn since.
  const onBoardPushed = useCallback(
    (doc, by) => {
      if (viewRoomRef.current) return;
      if (isInstructor && by === currentUser.guid) return;
      const payload = doc.items || {};
      board.replaceAll(payload.items || [], payload.strokes || []);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isInstructor, currentUser.guid]
  );

  // Board events from the class stream are the class board's; on a room's
  // board they are not ours to apply.
  const onClassBoardOps = useCallback(
    (ops, by) => {
      if (viewRoomRef.current) return;
      onBoardOps(ops, by);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentUser.guid, board.applyOp]
  );
  const onClassBoardLive = useCallback(
    (kind, payload, by) => {
      if (viewRoomRef.current) return;
      onBoardLive(kind, payload, by);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentUser.guid]
  );

  const onBoardOps = useCallback(
    (ops, by) => {
      // Our own operations are already on screen; replaying them would undo
      // anything drawn in the moment since.
      if (by === currentUser.guid) return;
      ops.forEach(board.applyOp);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentUser.guid, board.applyOp]
  );

  const onBoardLive = useCallback(
    (kind, payload, by) => {
      if (by === currentUser.guid) return;
      if (kind === "stroke.progress") setLiveStrokes((prev) => ({ ...prev, [payload.id]: payload }));
      if (kind === "stroke.ended") {
        setLiveStrokes((prev) => {
          const next = { ...prev };
          delete next[payload.id];
          return next;
        });
      }
    },
    [currentUser.guid]
  );

  const room = useRoom({
    sessionGuid,
    currentUser,
    isInstructor,
    onBoardPushed,
    onBoardOps: onClassBoardOps,
    onBoardLive: onClassBoardLive,
    onSubRooms: subRooms.applyAnnounced,
    onActivity: live.poke,
    onSessionEnded: () => {
      if (!isInstructor) setClassEnded(true);
    },
  });
  // A room's own stream, while one is on screen: its board, as the others
  // in it change it.
  useRoomStream({
    sessionGuid,
    subRoomGuid: viewRoomId,
    enabled: Boolean(sessionGuid && viewRoomId),
    handlers: { onBoardOps, onBoardLive },
  });
  const { clusters, now } = useQuestionClusters(room.questions);
  const boardSync = useBoardSync({ sessionGuid, subRoomGuid: viewRoomId, board, isInstructor });

  // Changing board: whatever was being drawn by others belongs to the one
  // left. Without a session the boards live here, one per room, swapped as
  // the view changes — the server does this when there is one.
  const localBoards = useRef({});
  const shownView = useRef(null);
  useEffect(() => {
    const key = viewRoomId || "class";
    setLiveStrokes({});
    if (sessionGuid) {
      shownView.current = key;
      return;
    }
    if (shownView.current && shownView.current !== key) {
      localBoards.current[shownView.current] = { items: board.items, strokes: board.strokes };
      const next = localBoards.current[key] || { items: [], strokes: [] };
      board.replaceAll(next.items, next.strokes);
    }
    shownView.current = key;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewRoomId, sessionGuid]);

  // A student is told where they have been sent, and when they are back.
  const announcedRoom = useRef(undefined);
  useEffect(() => {
    if (isInstructor) return;
    const was = announcedRoom.current;
    announcedRoom.current = viewRoomId;
    if (was === undefined || was === viewRoomId) return;
    toast({
      title: viewRoom ? `You're in ${viewRoom.name}` : "Back with the class",
      description: viewRoom ? "This board is just for your room." : undefined,
      status: "info",
      duration: 3000,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewRoomId, isInstructor]);
  const { viewport, zoomAt, zoomBy, panBy, fitTo, reset, startPan } = useViewport();

  const [tool, setTool] = useState(TOOLS.SELECT);
  /** Placing one thing arms the cursor again, so the next click moves what
   *  was just placed rather than dropping another one beside it. */
  const releaseTool = useCallback(
    () => setTool((prev) => (REVERTS_AFTER_USE.has(prev) ? TOOLS.SELECT : prev)),
    []
  );
  const [shape, setShape] = useState(SHAPES.SQUARE);
  const [colour, setColour] = useState(DEFAULT_COLOUR);
  const [pattern, setPattern] = useState("dots");
  // Strokes other people are drawing right now: shown, never stored, and
  // dropped the moment the finished stroke arrives as an operation.
  const [liveStrokes, setLiveStrokes] = useState({});
  // Which section of the dock is open, if any. Folded to start: the canvas
  // is the room, and the badges say when a section is worth opening.
  const [dockOpen, setDockOpen] = useState(null);
  // Messages already seen, so the Chat badge counts only what is new. Read
  // up to date whenever the chat is open.
  const [seenMessages, setSeenMessages] = useState(0);
  useEffect(() => {
    if (dockOpen === "chat") setSeenMessages(room.messages.length);
  }, [dockOpen, room.messages.length]);
  const fileInput = useRef(null);

  // The class's students. The server leaves the teacher out of the roster;
  // this makes sure, so the teacher is never counted as attending their own
  // class or dealt into a sub-room.
  const students = room.roster.filter((p) => p.guid !== currentUser.guid);
  const attendingGuids = students.filter((p) => p.isPresent).map((p) => p.guid);

  /** Everything on the board, as one rectangle — what Fit frames. */
  const contentBounds = useMemo(() => {
    const boxes = [
      ...board.items.filter((it) => it.kind !== "connector").map(boundsOf),
      ...board.strokes
        .filter((st) => st.points?.length)
        .map((st) => {
          const xs = st.points.map((pt) => pt.x);
          const ys = st.points.map((pt) => pt.y);
          return {
            x: Math.min(...xs),
            y: Math.min(...ys),
            width: Math.max(...xs) - Math.min(...xs) || 1,
            height: Math.max(...ys) - Math.min(...ys) || 1,
          };
        }),
    ];
    if (!boxes.length) return null;
    const x = Math.min(...boxes.map((b) => b.x));
    const y = Math.min(...boxes.map((b) => b.y));
    return {
      x,
      y,
      width: Math.max(...boxes.map((b) => b.x + b.width)) - x,
      height: Math.max(...boxes.map((b) => b.y + b.height)) - y,
    };
  }, [board.items, board.strokes]);

  const fitContent = useCallback(
    (size) => (contentBounds ? fitTo(contentBounds, size) : reset()),
    [contentBounds, fitTo, reset]
  );

  // Anything placed from the activity studio lands in the middle of what is
  // on screen, in board coordinates.
  const viewCentre = () => ({
    x: (window.innerWidth / 2 - viewport.x) / viewport.zoom,
    y: (window.innerHeight / 2 - viewport.y) / viewport.zoom,
  });
  const questionItem = (q, x, y) => ({
    kind: "question",
    x,
    y,
    width: 380,
    text: q.question_text,
    marks: q.total_mark,
    label: q.paper_code ? questionLabel(q) : `Question ${q.number}${q.letter ? q.letter : ""}`,
    topic: q.topic_name || null,
    image: q.image_1 || null,
    source: [q.paper_name, q.exam_series_year].filter(Boolean).join(" · ") || null,
  });
  const dropOnBoard = (questions) => {
    const centre = viewCentre();
    const gap = 24;
    const width = questions.length * 380 + (questions.length - 1) * gap;
    questions.forEach((q, i) => board.add(questionItem(q, centre.x - width / 2 + i * (380 + gap), centre.y - 100)));
    setDockOpen(null);
    toast({ title: `${questions.length === 1 ? "Question" : `${questions.length} questions`} on the board`, status: "success", duration: 2200 });
  };
  const reteach = (question, insight) => {
    const centre = viewCentre();
    if (question) board.add(questionItem(question, centre.x - 400, centre.y - 100));
    if (insight?.misconception) {
      board.add({
        kind: "sticky",
        x: centre.x + 20,
        y: centre.y - 100,
        colour: colour,
        text: `Most said: ${insight.misconception}${insight.reteach ? `\n\n${insight.reteach}` : ""}`,
      });
    }
    setStage(null);
    toast({ title: "On the board to reteach", status: "success", duration: 2200 });
  };
  const clearActivity = async () => {
    await live.act((api, s, id) => api.clear(s, id));
    setStage(null);
  };
  const activityOn = live.activity && live.activity.status !== "ARCHIVED";
  const needsEyes = (live.activity?.responses || []).filter((r) => r.needs_review && !r.released).length;

  const unread = Math.max(0, room.messages.length - seenMessages);
  const dockSections = [
    {
      id: "people",
      label: "People",
      icon: HiOutlineUsers,
      badge: attendingGuids.length || null,
      grow: false,
      render: () => (
        <AttendancePanel roster={students} attendingGuids={attendingGuids} isDark={isDark} />
      ),
    },
    {
      id: "chat",
      label: "Chat",
      title: "Session chat",
      icon: HiOutlineChatBubbleLeftRight,
      badge: unread || null,
      attention: unread > 0,
      grow: true,
      render: () => (
        <SessionChatPanel messages={room.messages} notice={room.error} onSend={room.send} />
      ),
    },
    ...(isInstructor
      ? [
          {
            id: "activity",
            label: "Activities",
            title: activityOn && !composing ? "Live activity" : "New activity",
            icon: HiOutlineBolt,
            badge: activityOn ? needsEyes || "•" : null,
            attention: needsEyes > 0,
            grow: true,
            width: 420,
            render: () =>
              activityOn && !composing ? (
                <LiveActivityPanel
                  live={live}
                  presenting={Boolean(stage)}
                  onPresent={() => setStage((v) => (v ? null : "live"))}
                  onShowResults={() => setStage("results")}
                  onNew={() => {
                    setStage(null);
                    setComposing(true);
                  }}
                />
              ) : (
                <ActivityStudio
                  sessionGuid={sessionGuid}
                  busy={false}
                  error={live.error}
                  onBack={activityOn ? () => setComposing(false) : undefined}
                  replacing={live.activity?.status === "LIVE"}
                  onDropOnBoard={(questions) => {
                    dropOnBoard(questions);
                    setComposing(false);
                  }}
                  onLaunch={async (body) => {
                    // The server ends whatever was on, so this is all a
                    // second activity takes.
                    const ok = await live.act((api, s) => api.launch(s, body));
                    if (ok) {
                      setComposing(false);
                      setStage("live");
                    }
                  }}
                />
              ),
          },
          {
            id: "rooms",
            label: "Sub-rooms",
            icon: HiOutlineSquares2X2,
            badge: subRooms.rooms.length || null,
            grow: true,
            render: () => (
              <RoomsPanel
                roster={students}
                subRooms={subRooms}
                viewRoomId={viewRoomId}
                onJoin={setTeacherRoomId}
                isDark={isDark}
              />
            ),
          },
        ]
      : []),
  ];
  /**
   * Open files onto the board on screen — the class's, or a sub-room's.
   *
   * Each picture (a PDF as its pages) is uploaded and then added as a board
   * item, so it reaches every screen in the room, and moves, resizes,
   * deletes and undoes like a note. Laid out in a row from the left of what
   * is on screen, clear of the dock.
   */
  const [opening, setOpening] = useState(null);
  const openFiles = async (fileList) => {
    if (!fileList?.length || opening) return;
    const origin = { x: (120 - viewport.x) / viewport.zoom, y: (150 - viewport.y) / viewport.zoom };
    setOpening({ done: 0, total: 0 });
    try {
      const { items, skipped, truncated } = await prepareContent(fileList, {
        upload: sessionGuid ? (file) => roomApi.uploadContent(sessionGuid, file) : null,
        onProgress: (done, total) => setOpening({ done, total }),
      });
      const width = 420;
      const gap = 28;
      items.forEach((it, i) =>
        board.add({
          kind: "content",
          x: origin.x + i * (width + gap),
          y: origin.y,
          width,
          height: Math.round(width * it.ratio),
          url: it.url,
          name: it.name,
          page: it.page,
          pages: it.pages,
        })
      );
      if (skipped.length) {
        toast({ title: "Only PDFs and images open on the board", description: skipped.join(", "), status: "warning", duration: 4000 });
      } else if (truncated) {
        toast({ title: "The first 40 pages are on the board", status: "info", duration: 3000 });
      }
    } catch (e) {
      console.error(e);
      toast({ title: "That file could not be opened", description: e.body?.detail || "Try again.", status: "error", duration: 4000 });
    } finally {
      setOpening(null);
    }
  };

  /** Answering a cluster posts once and clears every question in it. */
  const respondToCluster = async (cluster, text) => {
    await room.send(text);
    await room.markAnswered(cluster.questions.map((q) => q.id));
    toast({
      title: `Answered ${cluster.count} ${cluster.count === 1 ? "student" : "students"}`,
      status: "success",
      duration: 2200,
    });
  };

  const dismissCluster = (cluster) => {
    room.markAnswered(cluster.questions.map((q) => q.id));
  };

  return (
    <Box
      className="stande-classroom"
      data-theme={isDark ? "dark" : "light"}
      position="relative"
      height="100vh"
      overflow="hidden"
      bg={c.surface}
      transition="background 0.2s"
    >
      <InClassCanvas
        viewport={viewport}
        onZoomAt={zoomAt}
        onZoomBy={zoomBy}
        onPanBy={panBy}
        onFit={fitContent}
        onResetView={reset}
        onStartPan={startPan}
        // Under the cursor a drag on empty canvas moves it; with a drawing
        // tool armed it draws. Someone who cannot draw can always move.
        panOnEmpty={tool === TOOLS.SELECT || !canDraw}
        pattern={pattern}
        onPatternChange={setPattern}
        isDark={isDark}
      >
        <BoardCanvas
          board={board}
          tool={tool}
          shape={shape}
          colour={colour}
          readOnly={!canDraw}
          scale={viewport.zoom}
          bare
          reach={BOARD_REACH}
          idleCursor={tool === TOOLS.SELECT || !canDraw ? "grab" : "default"}
          liveStrokes={Object.values(liveStrokes)}
          onStrokeProgress={(stroke) => boardOps.emitLive("stroke.progress", stroke)}
          onStrokeEnded={(id) => boardOps.emitLive("stroke.ended", { id })}
          onToolUsed={releaseTool}
        />
      </InClassCanvas>

      <InClassHeader
        classroomName={classroomName}
        topic={sessionName}
        isDark={isDark}
        readOnly={!isInstructor}
        onBack={backToSpace}
        onOpenContent={() => fileInput.current?.click()}
        onEndSession={isInstructor && sessionGuid && isLive ? endSession : undefined}
        onGoLive={isInstructor && sessionGuid && !isLive && sessionState !== "DONE" ? goLive : undefined}
        opening={opening}
        fullscreen={fullscreen}
      />

      <Input
        ref={fileInput}
        type="file"
        multiple
        accept="image/*,application/pdf"
        display="none"
        onChange={(e) => {
          const files = [...e.target.files];
          e.target.value = "";
          openFiles(files);
        }}
      />

      {/* Under the class name: which room you are in, when it is not the
          class, and — for the teacher — what to cover next. */}
      <Flex
        position="absolute"
        top={BELOW_HEADER}
        left="50%"
        transform="translateX(-50%)"
        width="min(520px, calc(100vw - 40px))"
        zIndex={18}
        direction="column"
        align="center"
        gap="8px"
        pointerEvents="none"
        sx={{ "& > *": { pointerEvents: "auto" } }}
      >
        {viewRoom && (
          <RoomBanner
            room={viewRoom}
            index={viewRoomIndex}
            isTeacher={isInstructor}
            currentUserGuid={currentUser.guid}
            onLeave={() => setTeacherRoomId(null)}
          />
        )}
        {isInstructor && (
          <Box width="100%">
            <FocusBanner
              clusters={clusters}
              now={now}
              isDark={isDark}
              onOpenCluster={() => {
                /* the rail is where a cluster is answered; this scrolls it into view */
                document.querySelector("[data-cluster-rail]")?.scrollIntoView({ block: "nearest" });
              }}
            />
          </Box>
        )}
      </Flex>

      {/* The big screen, for the class to read from the back of the room. */}
      {isInstructor && stage && activityOn && (
        <ActivityStage live={live} mode={stage} onClose={() => setStage(null)} onReteach={reteach} onClear={clearActivity} />
      )}
      {/* A student's side of it: the questions, their answers, their feedback. */}
      {!isInstructor && <StudentActivity live={live} />}

      {/* The teacher ended the class: say so, and the way back. */}
      {classEnded && (
        <Flex position="absolute" inset="0" zIndex={30} align="center" justify="center" bg="rgba(34,34,34,0.32)" px="16px" data-no-pan>
          <Box w="min(420px, 100%)" p="24px" borderRadius="22px" bg={c.page} border="1px solid" borderColor={c.borderSoft}
            boxShadow="0 24px 64px -24px rgba(34,34,34,0.4)" textAlign="center">
            <Text fontSize="18px" fontWeight="600" color={c.text}>The class has ended</Text>
            <Text fontSize="13px" color={c.textSub} mt="6px">
              Your teacher ended this session. Its recap, and any work set after it, are in the space.
            </Text>
            <Flex gap="8px" justify="center" mt="16px">
              <Button size="sm" h="38px" px="14px" borderRadius="12px" variant="ghost" color={c.textSub} onClick={() => setClassEnded(false)}>
                Stay on the board
              </Button>
              <Button size="sm" h="38px" px="16px" borderRadius="12px" bg={c.solidBg} color={c.solidText} _hover={{ opacity: 0.86 }} onClick={backToSpace}>
                Back to the space
              </Button>
            </Flex>
          </Box>
        </Flex>
      )}

      {/* The open rooms, along the bottom — the teacher's way between them. */}
      {isInstructor && subRooms.isOpen && (
        <RoomStrip rooms={subRooms.rooms} viewRoomId={viewRoomId} onJoin={setTeacherRoomId} />
      )}

      {/* The room down the left: who is here and what is being said, folded
          to a rail of icons, with the question groups under it. */}
      <RoomDock sections={dockSections} openId={dockOpen} onOpen={setDockOpen} top={BELOW_HEADER} left={SIDE_GAP}>
        <Box data-cluster-rail data-no-pan>
          <ClusterRail
            clusters={clusters}
            now={now}
            isDark={isDark}
            onRespond={respondToCluster}
            onDismiss={dismissCluster}
            position="relative"
          />
        </Box>
      </RoomDock>

      {/* The tools, low on the right, above the canvas's own controls. */}
      {canDraw && (
        <Box position="absolute" right={SIDE_GAP} bottom="76px" zIndex={20}>
          <BoardToolbar
            tool={tool}
            onToolChange={setTool}
            shape={shape}
            onShapeChange={setShape}
            colour={colour}
            onColourChange={setColour}
            onClear={board.clear}
            onUndo={board.undo}
            canClear={!board.isEmpty}
            isDark={isDark}
          />
        </Box>
      )}

      {/* the theme toggle lives in the classroom header elsewhere; keep it
          reachable here so a full-page room is not stuck in one mode */}
      <Box
        as="button"
        type="button"
        position="fixed"
        bottom="18px"
        left="18px"
        zIndex={20}
        px="11px"
        py="6px"
        borderRadius="200px"
        {...glassSurface}
        color={c.textSub}
        fontSize="11.5px"
        onClick={toggleColorMode}
        _hover={{ color: c.text }}
      >
        {isDark ? "Light mode" : "Dark mode"}
      </Box>
    </Box>
  );
};

export default InClassPage;
